import React, { useEffect, useState, Suspense } from 'react';
import { Helmet } from 'react-helmet';
import Apiservice from '../services/Apiservice';
import { Link } from 'react-router-dom';
import CommonFunctions from '../components/CommonFunctions';
import { RenderNavigation, RenderFooter, RenderLeftAside, RenderRightAside } from '../components/ReusableFragments';

const Slider = React.lazy(() => import('react-slick'));

const Homepage = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [tournamentId] = useState('3');
  const [error, setError] = useState(null);
  const [upcomingMatches, setUpcomingMatches] = useState([]);
  const [resultedMatches, setResultedMatches] = useState([]);
  const [todayMatches, setTodayMatches] = useState([]);
  const [matchStatus, setMatchStatus] = useState({});
  const [playerBdays, setPlayerBdays] = useState([]);
  const [todaySfs, setTodaySfs] = useState({});
  const [pointsTable, setPointsTable] = useState([]);
  const [topBatters, setTopBatters] = useState([]);
  const [topBowlers, setTopBowlers] = useState([]);
  const [posts, setPosts] = useState([]);

  const fetchData = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await Apiservice.getMatchFixtures(tournamentId);
      // console.log('Raw response:', response);

      let data = {};
      if (typeof response.data === 'string') {
        const jsonParts = response.data.split('}{');
        let matchStatusParsed = false;
        jsonParts.forEach((part, index) => {
          let jsonString = part;
          if (index > 0) jsonString = '{' + jsonString;
          if (index < jsonParts.length - 1) jsonString = jsonString + '}';
          try {
            const parsedPart = JSON.parse(jsonString);
            if (index === 0 && parsedPart.matchstatus) {
              data.matchstatus = parsedPart.matchstatus;
              matchStatusParsed = true;
            } else {
              Object.keys(parsedPart).forEach(key => {
                if (key !== 'matchstatus' || !matchStatusParsed) {
                  data[key] = parsedPart[key];
                }
              });
            }
          } catch (e) {
            console.error('Error parsing JSON part:', jsonString, e);
          }
        });
      } else {
        data = response.data;
      }

      // console.log('Parsed data:', JSON.stringify(data, null, 2));

      if (data.upcoming_matches || data.resulted_matches || data.total_sfs || data.points_table || data.bdays) {
        const resultedMatchesData = Object.keys(data.resulted_matches || {}).map(matchId => {
          const matchTeams = data.resulted_matches[matchId];
          return {
            match_id: matchId.replace('match_id_', ''),
            formatted_match_no: matchTeams[0]?.formatted_match_no || '',
            team1_id: matchTeams[0]?.team_id || '',
            team1_name: matchTeams[0]?.team_shortcode || 'Unknown',
            team1_shortcode: matchTeams[0]?.team_shortcode || 'T1',
            total_scored_runs_team1: matchTeams[0]?.total_scored_runs || '0',
            total_extras_team1: matchTeams[0]?.total_extras || '0',
            total_out_count_team1: matchTeams[0]?.total_out_count || '0',
            team2_id: matchTeams[1]?.team_id || '',
            team2_name: matchTeams[1]?.team_shortcode || 'Unknown',
            team2_shortcode: matchTeams[1]?.team_shortcode || 'T2',
            total_scored_runs_team2: matchTeams[1]?.total_scored_runs || '0',
            total_extras_team2: matchTeams[1]?.total_extras || '0',
            total_out_count_team2: matchTeams[1]?.total_out_count || '0',
            venue_name: matchTeams[0]?.venue_name || 'TBD',
            city: matchTeams[0]?.city || ''
          };
        });

        setUpcomingMatches(data.upcoming_matches || []);
        setResultedMatches(resultedMatchesData || []);
        setTodayMatches(data.today_matches || []);
        setMatchStatus(data.matchstatus || {});
        setTodaySfs(data.total_sfs?.[0] || {});
        setPointsTable(data.points_table || []);
        setPlayerBdays(data.bdays || []);
        setTopBatters(data.topbatters || []);
        setTopBowlers(data.topbowlers || []);
        setPosts(data.posts || []);

        document.title = todayMatches.length 
          ? `Live WPL 2024: ${todayMatches[0].team1_shortcode} vs ${todayMatches[0].team2_shortcode}`
          : "Women's Premier League 2024 | WPL Cricinfo";
        const metaDescription = document.querySelector('meta[name="description"]');
        if (metaDescription) {
          metaDescription.content = todayMatches.length 
            ? `Live updates from ${todayMatches[0].team1_shortcode} vs ${todayMatches[0].team2_shortcode} at ${todayMatches[0].venue_name}.`
            : "Get live updates, match results, player stats, and more from WPL 2024.";
        }
      } else {
        setError("No valid data available.");
      }
    } catch (error) {
      setError("Error fetching data. Please try again.");
      // console.error("Error fetching data:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [tournamentId]);

  useEffect(() => {
    // console.log('State Updated:', { upcomingMatches, resultedMatches, todayMatches, matchStatus, posts, playerBdays, pointsTable, topBatters, topBowlers, todaySfs });
  }, [upcomingMatches, resultedMatches, todayMatches, matchStatus, posts, playerBdays, pointsTable, topBatters, topBowlers, todaySfs]);

  const mainSliderSettings = {
    dots: true,
    infinite: true,
    speed: 500,
    slidesToShow: 3,
    slidesToScroll: 1,
    autoplay: true,
    autoplaySpeed: 2000,
    responsive: [
      { breakpoint: 768, settings: { slidesToShow: 2, slidesToScroll: 1 } },
      { breakpoint: 480, settings: { slidesToShow: 1, slidesToScroll: 1 } }
    ]
  };

  const renderLoaderOrError = () => (
    <>
      {isLoading && (
      <div className="progress" style={{height: "4px"}}>
        <div 
          className="progress-bar progress-bar-animated" 
          role="progressbar" 
          style={{ width: '100%' }} 
          aria-valuenow={100} 
          aria-valuemin={0} 
          aria-valuemax={100}>
        </div>
      </div>
    )}
      {error && <p className="text-danger">Error: {error}</p>}
      {!isLoading && !error && !resultedMatches.length && !upcomingMatches.length && <p>No matches to display.</p>}
    </>
  );

  const renderMatchStatus = () => {
    if (!matchStatus || matchStatus.result === 0) {
      return <p className="text-muted m-0">No live matches of Women's Premier League at this time.</p>;
    }
    return Object.keys(matchStatus).map((inningsId) => {
      const status = matchStatus[inningsId];
      return (
        <div className="card mb-2" key={inningsId}>
          <div className="card-body p-2">
            <h5 className="card-title fs-6">{status.team1_name} vs {status.team2_name}</h5>
            <p className="mb-1">
              Innings {status.innings_number}: {status.total_runs_scored}/{status.total_outs} 
              ({status.total_overs_bowled} overs)
            </p>
            <small>Extras: {status.total_wides_bowled + status.total_no_balls_bowled}</small>
          </div>
        </div>
      );
    });
  };

  const renderPosts = () => {
    if (!posts?.length) {
      return <div>No posts available.</div>;
    }
    return posts.map(item => (
      <div className="col-md-6 mb-4" key={item.id}>
        <div className="card shadow h-100">
          <img src={`https://wplcricinfo.com/wpl/admin/${item.imgUrl}`} className="card-img-top" alt={item.title} loading="lazy" />
          <div className="card-body p-2 h-100">
            <h3 className="card-title fs-6">
              <Link className="text-decoration-none" to={`/post/${item.id}/${CommonFunctions.formatPostTitleForLink(item.title)}`}>{item.title}</Link>
            </h3>
          </div>
          <div className="card-footer">
            <small className="text-body-secondary">Posted on: {CommonFunctions.getPostDateFormat(item.created_at)}</small>
          </div>
        </div>
      </div>
    ));
  };

  return (
    <React.Fragment>
      <Helmet>
        <title>WPL Cricinfo | WPL 2025, IPL 2025 Latest Updates and Fixtures & Results</title>
        <meta name="description" content="Check the latest IPL 2025 Results, WPL 2025 fixtures, upcoming matches and results." />
        <meta name="keywords" content="WPL 2025, IPL 2025, fixtures, results, cricket schedule" />
      </Helmet>
      {renderLoaderOrError()}
      <div className="bg-fluid"></div>
      <RenderNavigation />
      <div className="container-fluid">
        <div className="row">
          <div className="col-md-12">
            <article className="mx-4 mb-3">
              <Suspense fallback={<div>Loading Slider...</div>}>
                <Slider {...mainSliderSettings}>
                  {["dc-w", "gg-w", "mi-w", "rcb-w", "upw-w"].map((banner, index) => (
                    <div className="p-1" key={index}>
                      <img src={`images/banner/${banner}_wpl_banner_wplcricinfo.webp`} alt={banner} className="img-fluid" loading="lazy" />
                    </div>
                  ))}
                </Slider>
              </Suspense>
            </article>
          </div>
        </div>
        <div className="row">
          <div className="col-md-3">
            <RenderLeftAside
              upcomingMatches={upcomingMatches}
              resultedMatches={resultedMatches}
              topBatters={topBatters}
              topBowlers={topBowlers}
            />
          </div>
          <div className="col-md-6">
            <div className="row mt-3">
              <div className="col-md-12">
                <div className="alert alert-info mb-0 shadow">
                  <h3 className="fs-5">Live Match Updates</h3>
                  {renderMatchStatus()}
                </div>
              </div>
            </div>
            <div className="row text-center my-3">
              {["dc-w", "gg-w", "mi-w", "rcb-w", "upw-w"].map((logo, index) => (
                <div className="col" key={index}>
                  <img src={`images/logos/${logo}-sm_wplcricinfo.jpg`} alt={logo} className="border border-3 rounded-circle shadow" loading="lazy" />
                  <div>{logo.toUpperCase()}</div>
                </div>
              ))}
            </div>
            <div className="row">
              {renderPosts()}
            </div>
          </div>
          <div className="col-md-3">
            <RenderRightAside
              todaySfs={todaySfs}
              pointsTable={pointsTable}
              playerBdays={playerBdays}
            />
            {/* Homepage-specific content */}
            <div className="card border shadow mb-2">
              <img src="images/banner/wpl-2024-trophy.webp" style={{ width: '100%' }} alt="WPL Trophy" loading="lazy" />
            </div>
          </div>
        </div>
      </div>
      <RenderFooter />
    </React.Fragment>
  );
};

export default Homepage;